import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnDestroy,
} from '@angular/core';
import { PerioChartFacade } from '@principle-theorem/ng-clinical-charting/store';
import { EmptyStateComponent } from '@principle-theorem/ng-principle-shared';
import { PipesModule } from '@principle-theorem/ng-shared';
import {
  IChartHistoryItem,
  IClinicalChart,
  IPerioRecord,
  type IPerioTableCell,
} from '@principle-theorem/principle-core/interfaces';
import { isSameRef, WithRef } from '@principle-theorem/shared';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { combineLatest, Observable, ReplaySubject, Subject } from 'rxjs';
import { bufferWhen, debounceTime, map, takeUntil } from 'rxjs/operators';
import { PerioChartCreateComponent } from '../perio-chart-create/perio-chart-create.component';
import { PerioTableComponent } from '../perio-table/perio-table.component';
import { PerioTable } from '../perio/perio-table';
import { PerioChartStore } from './perio-chart.store';

@Component({
  selector: 'pr-perio-chart-ui',
  templateUrl: './perio-chart-ui.component.html',
  styleUrls: ['./perio-chart-ui.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    PerioTableComponent,
    NgxSkeletonLoaderModule,
    PipesModule,
    PerioChartCreateComponent,
    EmptyStateComponent,
  ],
  providers: [PerioChartStore],
})
export class PerioChartUiComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  private _perioChartFacade = inject(PerioChartFacade);
  public perioChartStore = inject(PerioChartStore);
  inputStopped$: Observable<void>;
  cellChanges$: Subject<IPerioTableCell> = new Subject();
  chart$ = new ReplaySubject<WithRef<IClinicalChart>>(1);
  chartHistory$ = new ReplaySubject<IChartHistoryItem[]>(1);
  compareChartHistory$: Observable<IChartHistoryItem[]>;

  @Input()
  set chart(chart: WithRef<IClinicalChart>) {
    if (chart) {
      this.chart$.next(chart);
    }
  }

  @Input()
  set chartHistory(chartHistory: IChartHistoryItem[]) {
    if (chartHistory) {
      this.chartHistory$.next(chartHistory);
    }
  }

  constructor() {
    this.inputStopped$ = this.cellChanges$.pipe(
      debounceTime(500),
      map(() => undefined)
    );

    this.cellChanges$
      .pipe(
        bufferWhen(() => this.inputStopped$),
        takeUntil(this._onDestroy$)
      )
      .subscribe((changes) => this._perioChartFacade.upsertPerioValue(changes));

    this.compareChartHistory$ = combineLatest([
      this.chartHistory$,
      this.chart$,
    ]).pipe(
      map(([history, chart]) =>
        history.filter((item) => !isSameRef(item.value.ref, chart?.ref))
      )
    );
  }

  handleChange(change: IPerioTableCell): void {
    this.cellChanges$.next(change);
  }

  deleteCells(cells?: IPerioTableCell[]): void {
    this._perioChartFacade.deletePerioValues(cells);
  }

  getLabel(label: string): string {
    return PerioTable.labelForPerioTable(label);
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  setPerioRecords(perioRecords: IPerioRecord[]): void {
    this._perioChartFacade.upsertPerioChart(perioRecords);
  }
}
